import React, { useState } from "react";
import { HelpCircle, Mail, Phone, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import "../../styles/settings/helpSupport.css";

// Button component
const Button = React.forwardRef(({ className, variant = "default", size = "default", ...props }, ref) => {
  const baseClass = "help-btn";
  const variantClass = `help-btn-${variant}`;
  const sizeClass = `help-btn-${size}`;
  const classes = `${baseClass} ${variantClass} ${sizeClass} ${className || ""}`;

  return <button className={classes} ref={ref} {...props} />;
});

// Card components
const Card = React.forwardRef(({ className, ...props }, ref) => <div ref={ref} className={`help-card ${className || ""}`} {...props} />);

const CardHeader = React.forwardRef(({ className, ...props }, ref) => <div ref={ref} className={`help-card-header ${className || ""}`} {...props} />);

const CardTitle = React.forwardRef(({ className, ...props }, ref) => <h3 ref={ref} className={`help-card-title ${className || ""}`} {...props} />);

const CardContent = React.forwardRef(({ className, ...props }, ref) => <div ref={ref} className={`help-card-content ${className || ""}`} {...props} />);

const HelpSupport = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  // FAQ data
  const faqs = [
    {
      id: 1,
      question: "How do I reset my password?",
      answer:
        "You can reset your password by going to the 'Change Password' section in your account settings. Enter your current password and then your new password twice to confirm.",
    },
    {
      id: 2,
      question: "How do I add a new payment method?",
      answer:
        "Navigate to the 'Wallet' section and click on 'Add New Payment Method'. You can add credit cards, debit cards, or other supported payment methods.",
    },
    {
      id: 3,
      question: "How do I change my subscription plan?",
      answer: "Go to the 'Manage Subscription' section where you can view your current plan and upgrade or downgrade as needed.",
    },
    {
      id: 4,
      question: "Is my data secure?",
      answer: "Yes, we use industry-standard encryption and security measures to protect your personal information and payment data.",
    },
    {
      id: 5,
      question: "How do I contact customer support?",
      answer: "You can reach us through email, phone, or live chat using the contact methods listed below.",
    },
  ];

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Support",
      description: "Get help via email",
      contact: "storelense@pragmasolutions.co",
      availability: "24/7",
    },
    {
      icon: Phone,
      title: "Phone Support",
      description: "Talk to our support team",
      contact: "+234 815 927 3522",
      availability: "Mon-Fri 9AM-6PM",
    },
    {
      icon: MessageCircle,
      title: "Live Chat",
      description: "Chat with us in real-time",
      contact: "Available on whatsapp",
      availability: "Mon-Fri 9AM-6PM",
    },
  ];

  const toggleFaq = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  return (
    <div className='cp-main-content'>
      <div style={{ width: "90%", maxWidth: "none" }} className='cp-content-container'>
        <div className='cp-header'>
          <div className='help-header-content'>
            <HelpCircle className='help-header-icon' />
            <div>
              <h1 className='cp-title privacy-data-info-title cp-title'>Help & Support</h1>
              <p className='cp-subtitle'>Find answers and get assistance</p>
            </div>
          </div>
        </div>

        <div className='help-content'>
          {/* Contact Methods */}
          <div className='help-section'>
            <h2 className='help-section-title'>Contact Us</h2>
            <div className='help-contact-grid'>
              {contactMethods.map((method, index) => (
                <Card key={index} className='help-contact-card'>
                  <CardContent className='help-contact-card-content'>
                    <div className='help-contact-icon'>
                      <method.icon className='help-icon' />
                    </div>
                    <div className='help-contact-info'>
                      <h3 className='help-contact-title'>{method.title}</h3>
                      <p className='help-contact-description'>{method.description}</p>
                      <p className='help-contact-details'>{method.contact}</p>
                      <p className='help-contact-availability'>{method.availability}</p>
                    </div>
                    <Button variant='outline' size='sm'>
                      Contact
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className='help-section'>
            <h2 className='help-section-title'>Frequently Asked Questions</h2>
            <div className='help-faq-container'>
              {faqs.map((faq) => (
                <Card key={faq.id} className='help-faq-card'>
                  <CardContent className='help-faq-card-content'>
                    <button className='help-faq-question' onClick={() => toggleFaq(faq.id)}>
                      <span>{faq.question}</span>
                      {expandedFaq === faq.id ? <ChevronUp className='help-faq-icon' /> : <ChevronDown className='help-faq-icon' />}
                    </button>
                    {expandedFaq === faq.id && (
                      <div className='help-faq-answer'>
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className='help-section'>
            <h2 className='help-section-title'>Quick Actions</h2>
            <div className='help-actions-grid'>
              <Button className='help-action-btn' variant='outline'>
                Download User Guide
              </Button>
              <Button className='help-action-btn' variant='outline'>
                Video Tutorials
              </Button>
              <Button className='help-action-btn' variant='outline'>
                System Status
              </Button>
              <Button className='help-action-btn' variant='outline'>
                Feature Requests
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;
